// lib/activity-cart.tsx - Activity Cart Context with localStorage persistence

'use client'

import { createContext, useContext, useEffect, useState, useCallback, useRef, ReactNode } from 'react'
import { v4 as uuidv4 } from 'uuid'
import { doc, getDoc } from 'firebase/firestore'
import { db as firebaseDb } from '@/lib/firebase'

// Types
export interface ActivityCartItem {
  cartItemId: string
  activityId: string
  activitySlug: string
  activityName: string
  activityImage: string
  date: string
  session?: string
  persons: number             // New: single person count
  pricePerPerson: number      // New: single price per person
  adults: number              // Backward compat (= persons)
  children: number            // Backward compat (always 0)
  totalGuests: number
  pricePerAdult: number       // Backward compat (= pricePerPerson)
  pricePerChild: number       // Backward compat
  subtotal: number
  addedAt: string
}

interface ActivityCartState {
  items: ActivityCartItem[]
  currency: 'USD'
  subtotal: number
  serviceFee: number
  total: number
  couponCode: string | null
  couponDiscount: number
  couponValid: boolean
}

interface ActivityCartContextValue extends ActivityCartState {
  addItem: (item: Omit<ActivityCartItem, 'cartItemId' | 'addedAt'>) => void
  removeItem: (cartItemId: string) => void
  updateItemQuantity: (cartItemId: string, adults: number, children: number) => void
  clearCart: () => void
  applyCoupon: (code: string) => Promise<boolean>
  clearCoupon: () => void
  itemCount: number
  isCartOpen: boolean
  setCartOpen: (open: boolean) => void
  isHydrated: boolean
  serviceFeePercent: number
}

// Constants
const CART_STORAGE_KEY = 'apex-activity-cart'
const CART_EXPIRY_HOURS = 24

// Context
const ActivityCartContext = createContext<ActivityCartContextValue | null>(null)

// Helper: Check if cart is expired
function isCartExpired(cart: ActivityCartState & { updatedAt?: string }): boolean {
  if (!cart.updatedAt) return true
  const updatedAt = new Date(cart.updatedAt)
  const now = new Date()
  const hoursDiff = (now.getTime() - updatedAt.getTime()) / (1000 * 60 * 60)
  return hoursDiff > CART_EXPIRY_HOURS
}

// Per-activity fee type
type ActivityFeeMap = Record<string, { enabled: boolean; percent: number }>

// Helper: Calculate totals with per-activity fees
function calculateTotals(
  items: ActivityCartItem[],
  couponDiscount: number = 0,
  activityFees: ActivityFeeMap
): { subtotal: number; serviceFee: number; total: number } {
  const subtotal = items.reduce((sum, item) => sum + item.subtotal, 0)
  const subtotalAfterCoupon = Math.max(0, subtotal - couponDiscount)

  // Per-activity service fee
  let totalFee = 0
  for (const item of items) {
    const fee = activityFees[item.activityId]
    if (fee?.enabled && fee.percent > 0) {
      totalFee += Math.round(item.subtotal * (fee.percent / 100) * 100) / 100
    }
  }

  return {
    subtotal,
    serviceFee: totalFee,
    total: subtotalAfterCoupon + totalFee
  }
}

// Provider
export function ActivityCartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ActivityCartItem[]>([])
  const [isCartOpen, setCartOpen] = useState(false)
  const [isHydrated, setIsHydrated] = useState(false)
  const [couponCode, setCouponCode] = useState<string | null>(null)
  const [couponDiscount, setCouponDiscount] = useState<number>(0)
  const [couponValid, setCouponValid] = useState<boolean>(false)
  const [activityFees, setActivityFees] = useState<ActivityFeeMap>({})
  const loadedFeeIds = useRef<Set<string>>(new Set())

  // Load per-activity fee configs from Firestore when items change
  useEffect(() => {
    const uniqueIds = [...new Set(items.map(i => i.activityId))]
    const newIds = uniqueIds.filter(id => !loadedFeeIds.current.has(id))
    if (newIds.length === 0) return

    async function loadFees() {
      const newFees: ActivityFeeMap = {}
      for (const id of newIds) {
        try {
          const actDoc = await getDoc(doc(firebaseDb, 'activityPricing', id))
          if (actDoc.exists()) {
            const data = actDoc.data()
            newFees[id] = data.serviceFee || { enabled: false, percent: 0 }
          } else {
            newFees[id] = { enabled: false, percent: 0 }
          }
          loadedFeeIds.current.add(id)
        } catch (err) {
          console.error(`[ActivityCart] Failed to load fee for ${id}:`, err)
          newFees[id] = { enabled: false, percent: 0 }
        }
      }
      setActivityFees(prev => ({ ...prev, ...newFees }))
    }
    loadFees()
  }, [items])

  // Load from localStorage on mount
  useEffect(() => {
    if (typeof window === 'undefined') return

    try {
      const stored = localStorage.getItem(CART_STORAGE_KEY)
      if (stored) {
        const cart = JSON.parse(stored)
        if (!isCartExpired(cart)) {
          setItems(cart.items || [])
          setCouponCode(cart.couponCode || null)
          setCouponDiscount(cart.couponDiscount || 0)
          setCouponValid(cart.couponValid || false)
        } else {
          localStorage.removeItem(CART_STORAGE_KEY)
        }
      }
    } catch (error) {
      console.error('Failed to load cart:', error)
    }

    setIsHydrated(true)
  }, [])

  // Save to localStorage on change
  useEffect(() => {
    if (!isHydrated || typeof window === 'undefined') return

    const totals = calculateTotals(items, couponDiscount, activityFees)
    const cart = {
      items,
      currency: 'USD' as const,
      ...totals,
      couponCode,
      couponDiscount,
      couponValid,
      updatedAt: new Date().toISOString()
    }

    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart))
  }, [items, isHydrated, couponCode, couponDiscount, couponValid, activityFees])

  const addItem = useCallback((item: Omit<ActivityCartItem, 'cartItemId' | 'addedAt'>) => {
    const newItem: ActivityCartItem = {
      ...item,
      cartItemId: uuidv4(),
      addedAt: new Date().toISOString()
    }

    setItems(prev => {
      // Check for duplicate (same activity + date + session)
      const existingIndex = prev.findIndex(
        i => i.activityId === item.activityId &&
             i.date === item.date &&
             i.session === item.session
      )

      if (existingIndex >= 0) {
        // Replace existing
        const updated = [...prev]
        updated[existingIndex] = newItem
        return updated
      }

      return [...prev, newItem]
    })

    setCartOpen(true)  // Open cart when adding
  }, [])

  const removeItem = useCallback((cartItemId: string) => {
    setItems(prev => prev.filter(item => item.cartItemId !== cartItemId))
  }, [])

  const updateItemQuantity = useCallback((
    cartItemId: string,
    adults: number,
    children: number
  ) => {
    setItems(prev => prev.map(item => {
      if (item.cartItemId !== cartItemId) return item

      const persons = adults + children
      const subtotal = persons * (item.pricePerPerson || item.pricePerAdult)
      return {
        ...item,
        persons,
        adults: persons,
        children: 0,
        totalGuests: persons,
        subtotal
      }
    }))
  }, [])

  const clearCart = useCallback(() => {
    setItems([])
    setCouponCode(null)
    setCouponDiscount(0)
    setCouponValid(false)
    localStorage.removeItem(CART_STORAGE_KEY)
  }, [])

  const applyCoupon = useCallback(async (code: string): Promise<boolean> => {
    if (items.length === 0) return false

    try {
      // Call API to validate coupon
      const apiBase = process.env.NEXT_PUBLIC_API_URL
      const apiUrl = apiBase ? `${apiBase}/api` : '/api'
      const subtotalAmount = items.reduce((sum, item) => sum + item.subtotal, 0)
      // Get all unique activity IDs in the cart
      const activityIds = [...new Set(items.map(i => i.activityId))]

      const response = await fetch(`${apiUrl}/activities/validate-coupon`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          code,
          subtotal: subtotalAmount,
          activityIds
        })
      })

      if (!response.ok) {
        setCouponValid(false)
        setCouponDiscount(0)
        setCouponCode(null)
        return false
      }

      const data = await response.json()
      if (data.success && data.data?.discount) {
        setCouponCode(code)
        setCouponDiscount(data.data.discount)
        setCouponValid(true)
        return true
      }

      setCouponValid(false)
      setCouponDiscount(0)
      setCouponCode(null)
      return false
    } catch (error) {
      console.error('Failed to validate coupon:', error)
      setCouponValid(false)
      setCouponDiscount(0)
      setCouponCode(null)
      return false
    }
  }, [items])

  const clearCoupon = useCallback(() => {
    setCouponCode(null)
    setCouponDiscount(0)
    setCouponValid(false)
  }, [])

  const totals = calculateTotals(items, couponDiscount, activityFees)

  // Compute a blended fee percent for display (weighted average across items)
  const blendedFeePercent = (() => {
    const totalSub = items.reduce((s, i) => s + i.subtotal, 0)
    if (totalSub === 0) return 0
    let weightedSum = 0
    for (const item of items) {
      const fee = activityFees[item.activityId]
      if (fee?.enabled && fee.percent > 0) {
        weightedSum += (item.subtotal / totalSub) * fee.percent
      }
    }
    return Math.round(weightedSum * 10) / 10
  })()

  const value: ActivityCartContextValue = {
    items,
    currency: 'USD',
    ...totals,
    couponCode,
    couponDiscount,
    couponValid,
    addItem,
    removeItem,
    updateItemQuantity,
    clearCart,
    applyCoupon,
    clearCoupon,
    itemCount: items.length,
    isCartOpen,
    setCartOpen,
    isHydrated,
    serviceFeePercent: blendedFeePercent
  }

  return (
    <ActivityCartContext.Provider value={value}>
      {children}
    </ActivityCartContext.Provider>
  )
}

// Hook
export function useActivityCart(): ActivityCartContextValue {
  const context = useContext(ActivityCartContext)
  if (!context) {
    throw new Error('useActivityCart must be used within ActivityCartProvider')
  }
  return context
}
